import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  getBulkEnrollReview,
  submitBulkEnrollments,
  type BulkEnrollCandidate,
  type BulkEnrollResult,
  type BulkEnrollReview,
  type PayerEnrollmentGroup,
} from '../../services/clinicsApi';
import { useStore } from '../../store';
import {
  enrollmentTimeframeLabel,
  enrollmentTypeLabel,
} from './enrollmentStatusLabels';
import { agencyTitle, nameClinicLines } from './payerEnrollments';
import { PayerName } from './PayerName';

interface StartEnrollmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The clinics in the tab's filter; empty means every clinic. */
  clinicIds: string[];
  /** Each clinic's display name, by id. */
  clinicNames: ReadonlyMap<string, string>;
  /** The tab's payers, to name each line as the table does. */
  payers: PayerEnrollmentGroup[];
  /** Reload the tab once something was submitted. */
  onSubmitted: () => void;
}

const GROUPS: { transaction: string; label: string }[] = [
  { transaction: 'institutionalClaimSubmission', label: 'Claims' },
  { transaction: 'eligibilityCheck', label: 'Eligibility' },
  { transaction: 'claimPayment', label: 'Remittance' },
];

const keyOf = (line: {
  agency_clinic_id: string;
  payor_id: string;
  transaction_type: string;
}) => `${line.agency_clinic_id}|${line.payor_id}|${line.transaction_type}`;

/** A line's payer as the table names it: the agency's insurances, then ID. */
const payerLabel = (
  line: BulkEnrollCandidate,
  byPayor: ReadonlyMap<string, PayerEnrollmentGroup>
) => {
  const payer = byPayor.get(line.payor_id);
  const title = payer ? agencyTitle(payer, [line.agency_clinic_id]) : null;
  return {
    title,
    first: title?.all[0] ?? line.payer_name,
    hover: title
      ? `${title.all.join(', ')} · paid by ${line.payer_name}`
      : line.payer_name,
    payerId: payer?.primary_payer_id ?? null,
  };
};

const looksLikeEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

type Stage = 'review' | 'confirm' | 'sending' | 'done';

/**
 * Start enrollments: every enrollment the clinics in view still need, in one
 * reviewed list. Roger staff only.
 *
 * Nothing is sent until the second confirmation. Remittance starts unticked
 * and carries its own warning, because enrolling it moves the agency's
 * remittance off whatever it uses today.
 */
export function StartEnrollmentsDialog({
  open,
  onOpenChange,
  clinicIds,
  clinicNames,
  payers,
  onSubmitted,
}: StartEnrollmentsDialogProps) {
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [rawReview, setReview] = useState<BulkEnrollReview | null>(null);
  const review = useMemo<BulkEnrollReview | null>(
    () =>
      rawReview && {
        ...rawReview,
        candidates: nameClinicLines(rawReview.candidates, clinicNames),
        clinic_gaps: nameClinicLines(rawReview.clinic_gaps, clinicNames),
      },
    [rawReview, clinicNames]
  );
  const byPayor = useMemo(
    () =>
      new Map(
        payers.flatMap(payer =>
          payer.payor_id ? [[payer.payor_id, payer] as const] : []
        )
      ),
    [payers]
  );
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [contactEmail, setContactEmail] = useState('');
  const [stage, setStage] = useState<Stage>('review');
  const [results, setResults] = useState<BulkEnrollResult[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReview(null);
    setStage('review');
    setResults([]);
    getBulkEnrollReview(clinicIds)
      .then(loaded => {
        if (cancelled) return;
        setReview(loaded);
        setContactEmail(loaded.contact_email);
        // Claims and eligibility on; remittance is a decision per clinic.
        setTicked(
          new Set(
            (loaded.candidates ?? [])
              .filter(
                line =>
                  !line.blocked_reason &&
                  line.transaction_type !== 'claimPayment'
              )
              .map(keyOf)
          )
        );
      })
      .catch(error => {
        console.error('Failed to load the enrollments to start', error);
        if (!cancelled) {
          showError('Unable to load enrollments to start. Please try again.');
          onOpenChange(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // clinicIds is read once per opening; the filter cannot change underneath.
  }, [open]);

  const candidates = useMemo(() => review?.candidates ?? [], [review]);
  const chosen = candidates.filter(line => ticked.has(keyOf(line)));
  const remittanceChosen = chosen.some(
    line => line.transaction_type === 'claimPayment'
  );
  const count = chosen.length;
  const plural = count === 1 ? '' : 's';

  const toggle = (keys: string[], on: boolean) =>
    setTicked(current => {
      const next = new Set(current);
      keys.forEach(key => (on ? next.add(key) : next.delete(key)));
      return next;
    });

  const submit = async () => {
    setStage('sending');
    try {
      setResults(
        await submitBulkEnrollments(
          chosen.map(line => ({
            agency_clinic_id: line.agency_clinic_id,
            payor_id: line.payor_id,
            transaction_type: line.transaction_type,
          })),
          contactEmail.trim()
        )
      );
      setStage('done');
      onSubmitted();
    } catch (error) {
      console.error('Failed to submit enrollments', error);
      showError('Unable to submit enrollments. Please try again.');
      setStage('review');
    }
  };

  const nameOf = (result: BulkEnrollResult) => {
    const line = candidates.find(
      candidate => keyOf(candidate) === keyOf(result)
    );
    return line
      ? `${payerLabel(line, byPayor).first} · ${line.clinic_name}`
      : 'Enrollment';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => stage !== 'sending' && onOpenChange(next)}
    >
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Start enrollments</DialogTitle>
          <DialogDescription>
            Submits each ticked enrollment to the payer.
          </DialogDescription>
        </DialogHeader>

        {!review ? (
          <p className='text-sm text-muted-foreground'>Loading…</p>
        ) : stage === 'done' ? (
          <Results results={results} nameOf={nameOf} />
        ) : (
          <div className='space-y-5 text-sm'>
            {(review.clinic_gaps ?? []).length > 0 && (
              <section className='space-y-1'>
                <h3 className='font-medium'>Can&apos;t be enrolled yet</h3>
                <ul className='list-disc space-y-0.5 pl-5 text-muted-foreground'>
                  {(review.clinic_gaps ?? []).map(gap => (
                    <li key={gap.agency_clinic_id}>
                      <span className='text-foreground'>{gap.clinic_name}</span>
                      : needs {gap.missing.join(', ')}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {candidates.length === 0 ? (
              <p className='text-muted-foreground'>
                {(review.clinic_gaps ?? []).length > 0 ? (
                  // Nothing to start, and clinics above are blocked: say
                  // where the fix is. Not "missing": a shared NPI is a
                  // wrong value, not an empty field.
                  <>
                    Fix the clinics above in{' '}
                    <a
                      href='/admin?section=clinic-info'
                      className='text-primary hover:underline'
                    >
                      Admin &gt; Clinics
                    </a>
                    , then start enrollments here.
                  </>
                ) : (
                  'Nothing left to start for these clinics.'
                )}
              </p>
            ) : (
              GROUPS.map(group => (
                <Group
                  key={group.transaction}
                  label={group.label}
                  lines={candidates.filter(
                    line => line.transaction_type === group.transaction
                  )}
                  ticked={ticked}
                  byPayor={byPayor}
                  onToggle={toggle}
                  disabled={stage !== 'review'}
                />
              ))
            )}

            {remittanceChosen && (
              <div className='flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                <p>
                  Enrolling remittance moves the agency&apos;s electronic
                  remittance to Roger and turns off its existing setup outside
                  Roger. Only do it when the agency is ready to switch.
                </p>
              </div>
            )}

            {candidates.length > 0 && (
              <label className='block space-y-1'>
                <span className='font-medium'>
                  Contact for enrollment questions
                </span>
                <Input
                  type='email'
                  value={contactEmail}
                  disabled={stage !== 'review'}
                  onChange={event => setContactEmail(event.target.value)}
                />
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          {stage === 'done' ? (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          ) : stage === 'review' ? (
            <>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                disabled={count === 0 || !looksLikeEmail(contactEmail)}
                onClick={() => setStage('confirm')}
              >
                Submit {count} enrollment{plural}
              </Button>
            </>
          ) : (
            <div className='flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
              <p className='text-sm text-muted-foreground sm:mr-auto'>
                This submits {count} enrollment{plural} to the payers. It
                can&apos;t be undone.
              </p>
              <Button
                variant='outline'
                disabled={stage === 'sending'}
                onClick={() => setStage('review')}
              >
                Back
              </Button>
              <Button
                disabled={stage === 'sending'}
                onClick={() => void submit()}
              >
                {stage === 'sending' ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Group({
  label,
  lines,
  ticked,
  byPayor,
  onToggle,
  disabled,
}: {
  label: string;
  lines: BulkEnrollCandidate[];
  ticked: Set<string>;
  byPayor: ReadonlyMap<string, PayerEnrollmentGroup>;
  onToggle: (keys: string[], on: boolean) => void;
  disabled: boolean;
}) {
  if (lines.length === 0) return null;
  const open = lines.filter(line => !line.blocked_reason).map(keyOf);
  const on = open.filter(key => ticked.has(key)).length;
  const groupState =
    on === 0 ? false : on === open.length ? true : ('indeterminate' as const);

  return (
    <section className='space-y-1'>
      <label className='flex items-center gap-2 font-medium'>
        <Checkbox
          checked={groupState}
          disabled={disabled || open.length === 0}
          onCheckedChange={() => onToggle(open, on !== open.length)}
          aria-label={`All ${label.toLowerCase()}`}
        />
        {label}
        <span className='font-normal text-muted-foreground'>
          {on} of {lines.length}
        </span>
      </label>
      <ul className='divide-y rounded-md border'>
        {lines.map(line => {
          const key = keyOf(line);
          const payer = payerLabel(line, byPayor);
          const type =
            line.enrollment_type || line.enrollment_timeframe
              ? `${enrollmentTypeLabel(line.enrollment_type)} · usually ${enrollmentTimeframeLabel(
                  line.enrollment_timeframe
                ).toLowerCase()}`
              : null;
          const detail = [payer.payerId ?? 'No payer ID', type]
            .filter(Boolean)
            .join(' · ');
          return (
            <li key={key}>
              <label className='flex items-start gap-2 px-3 py-2'>
                <Checkbox
                  className='mt-0.5'
                  checked={ticked.has(key)}
                  disabled={disabled || Boolean(line.blocked_reason)}
                  onCheckedChange={value => onToggle([key], value === true)}
                  aria-label={`${payer.first}, ${line.clinic_name}`}
                />
                <div className='min-w-0'>
                  <div className='flex min-w-0' title={payer.hover}>
                    <PayerName title={payer.title} fallback={line.payer_name} />
                    <span className='shrink-0 whitespace-nowrap text-muted-foreground'>
                      &nbsp;· {line.clinic_name}
                    </span>
                  </div>
                  {line.blocked_reason ? (
                    <div className='text-xs text-amber-700'>
                      {line.blocked_reason}
                    </div>
                  ) : (
                    <div className='text-xs text-muted-foreground'>
                      {detail}
                    </div>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Results({
  results,
  nameOf,
}: {
  results: BulkEnrollResult[];
  nameOf: (result: BulkEnrollResult) => string;
}) {
  const submitted = results.filter(result => result.outcome === 'submitted');
  const notSent = results.filter(result => result.outcome !== 'submitted');
  return (
    <div className='space-y-3 text-sm'>
      <p>
        <span className='font-medium text-green-700'>
          {submitted.length} submitted.
        </span>{' '}
        They show in the table as waiting on the payer, or as needing action.
      </p>
      {notSent.length > 0 && (
        <section className='space-y-1'>
          <h3 className='font-medium'>Not sent</h3>
          <ul className='list-disc space-y-0.5 pl-5'>
            {notSent.map(result => (
              <li key={keyOf(result)}>
                {nameOf(result)}
                <span
                  className={
                    result.outcome === 'failed'
                      ? 'text-red-600'
                      : 'text-muted-foreground'
                  }
                >
                  {' '}
                  — {result.message ?? 'Not sent'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
